import { ListPlus, Lock } from "lucide-react";
import * as React from "react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { DYE, clothesline, pegAt } from "../../figures/builds";
import { Dye } from "../../figures/engine";
import { ListSummary } from "../../types/List";
import Work, { WorkType } from "../../types/Work";
import CordFigure from "../ui/CordFigure";
import Cover from "../works/Cover";
import WorkThumb from "../works/WorkThumb";

const count = (n: number) => `${n} ${n === 1 ? "title" : "titles"}`;
export const followers = (n: number) =>
  `${n} ${n === 1 ? "follower" : "followers"}`;

// A list's first covers fanned out like a hand of cards, the first on top.
// Each is an ordinary cover (same corners and ring as everywhere else); with
// none, one dashed placeholder around `empty` (an empty list's icon).
export const CoverStack: React.FC<{
  works: Work[];
  className?: string;
  empty?: React.ReactNode;
}> = ({ works, className, empty = <ListPlus size="40%" /> }) => (
  <span
    className={`cover-stack${className ? ` ${className}` : ""}`}
    aria-hidden="true"
  >
    {works.length === 0 ? (
      <span className="work-thumb cover-stack-empty">{empty}</span>
    ) : (
      works
        .slice(0, 4)
        .map((work, i) => (
          <WorkThumb
            key={work.id}
            work={work}
            className="cover-stack-item"
            style={{ "--i": i } as React.CSSProperties}
          />
        ))
    )}
  </span>
);

// The dye of whatever a list mostly holds (the first to lead, on a tie).
export const lineDye = (works: Work[]): Dye => {
  const counts = new Map<WorkType, number>();
  for (const work of works)
    counts.set(work.workType, (counts.get(work.workType) ?? 0) + 1);
  let top = works[0].workType;
  counts.forEach((n, type) => {
    if (n > counts.get(top)!) top = type;
  });
  return DYE[top];
};

// A list's first covers knotted along a line in its order (numbered, on the
// list's own page), the line and its knots dyed for what the list mostly holds.
export const ListLine: React.FC<{
  works: Work[];
  numbered?: boolean;
  className?: string;
}> = ({ works, numbered = false, className = "list-line" }) => {
  const dye = lineDye(works);
  // Keyed by the covers, not just how many: the figure finds what to hang
  // when it lays out, so a new set of as many (a diary's best as more of
  // it loads) must lay it out again, or the new covers hang nowhere.
  const pegged = works.map((w) => w.id).join(",");
  const build = useMemo(
    () => clothesline(works.length, dye, 18),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pegged, dye],
  );
  return (
    <CordFigure
      className={`line-fig ${className}`}
      build={build}
      style={{ "--hitch-dye": `var(${dye})` } as React.CSSProperties}
      aria-hidden="true"
    >
      {works.map((work, q) => (
        <span
          key={work.id}
          className="peg"
          data-at={`line:${pegAt(q, works.length)}:peg`}
        >
          <Cover work={work} />
          {numbered && <b className="peg-no">{q + 1}</b>}
        </span>
      ))}
    </CordFigure>
  );
};

// A list in an index: its covers on a line, its name and size, and who made
// it where lists by different people mix.
const ListCard: React.FC<{ list: ListSummary; showPrivacy?: boolean }> = ({
  list,
  showPrivacy = false,
}) => (
  <Link to={`/lists/${list.id}`} className="list-card">
    {list.preview.length > 0 ? (
      <ListLine works={list.preview} />
    ) : (
      <span className="list-line list-line-empty" aria-hidden="true">
        <ListPlus size={26} />
      </span>
    )}
    <span className="list-card-name">{list.name}</span>
    <span className="list-card-meta muted">
      {list.owner && `by @${list.owner.username} · `}
      {count(list.itemCount)}
      {list.followerCount > 0 && ` · ${followers(list.followerCount)}`}
      {showPrivacy && list.visibility === "private" && (
        <>
          {" · "}
          <Lock size={11} aria-hidden="true" /> Private
        </>
      )}
    </span>
  </Link>
);

export default ListCard;
